import React from "react";

/**
 * Rudra Roast pouch renderer.
 * - If the blend has a real product photo (image_url), mount it on a natural
 *   coffee-estate backdrop with a soft leaf glow.
 * - Otherwise fall back to a hand-stamped SVG pouch (for custom blends).
 */
export default function Pouch({ blend, weightLabel = "250 G", className = "" }) {
  const hasPhoto = !!(blend?.image_url && blend.image_url.trim());
  if (hasPhoto) return <PouchPhoto blend={blend} className={className} />;
  return <PouchSvg blend={blend} weightLabel={weightLabel} className={className} />;
}

function PouchPhoto({ blend, className = "" }) {
  return (
    <div
      className={`relative w-full h-full flex items-center justify-center overflow-hidden ${className}`}
      data-testid="pouch-photo"
      style={{
        background:
          "radial-gradient(ellipse at 50% 40%, rgba(122, 132, 113, 0.28), transparent 65%), radial-gradient(ellipse at 50% 100%, rgba(46, 93, 63, 0.22), transparent 60%), linear-gradient(180deg, #F0EBD6 0%, #E4DDC2 100%)",
      }}
    >
      {/* Sage leaf silhouettes */}
      <svg className="absolute -left-6 -bottom-8 opacity-25" width="180" height="180" viewBox="0 0 180 180" aria-hidden>
        <g fill="none" stroke="#4A7A4A" strokeWidth="1.4">
          <ellipse cx="90" cy="90" rx="66" ry="26" transform="rotate(-25 90 90)" />
          <ellipse cx="90" cy="90" rx="66" ry="26" transform="rotate(25 90 90)" />
        </g>
      </svg>
      <svg className="absolute -right-8 -top-6 opacity-20" width="160" height="160" viewBox="0 0 160 160" aria-hidden>
        <g fill="none" stroke="#7A8471" strokeWidth="1.2">
          <ellipse cx="80" cy="80" rx="60" ry="24" transform="rotate(-30 80 80)" />
          <ellipse cx="80" cy="80" rx="60" ry="24" transform="rotate(30 80 80)" />
          <circle cx="80" cy="80" r="6" fill="#6B4423" />
        </g>
      </svg>

      <img
        src={blend.image_url}
        alt={blend.name}
        loading="lazy"
        className="relative z-10 max-h-[92%] max-w-[88%] object-contain"
        style={{ filter: "drop-shadow(0 24px 30px rgba(42, 31, 19, 0.35))" }}
      />

      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
    </div>
  );
}

function PouchSvg({ blend, weightLabel = "250 G", className = "" }) {
  const name = blend?.name || "SIGNATURE";
  const notes = blend?.flavor_notes?.slice(0, 3).join(" · ") || "STRONG · CLASSIC BLEND";
  const composition = blend
    ? [
        blend.arabica_pct ? `${blend.arabica_pct}% ARABICA` : null,
        blend.robusta_pct ? `${blend.robusta_pct}% ROBUSTA` : null,
        blend.chicory_pct ? `${blend.chicory_pct}% CHICORY` : null,
      ]
        .filter(Boolean)
        .join("  ·  ")
    : "";

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center overflow-hidden ${className}`}
      data-testid="pouch"
      style={{ background: "linear-gradient(180deg, #F0EBD6 0%, #E4DDC2 100%)" }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(122, 132, 113, 0.22), transparent 65%), radial-gradient(ellipse at 50% 100%, rgba(46, 93, 63, 0.18), transparent 60%)",
        }}
      />
      <svg
        viewBox="0 0 400 520"
        preserveAspectRatio="xMidYMid meet"
        className="relative z-10 h-[92%] w-auto"
        style={{ filter: "drop-shadow(0 20px 30px rgba(42, 31, 19, 0.30))" }}
      >
        <defs>
          <linearGradient id="body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E4DDC2" />
            <stop offset="55%" stopColor="#D3C6A2" />
            <stop offset="100%" stopColor="#B8A87F" />
          </linearGradient>
          <linearGradient id="sheen" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="45%" stopColor="rgba(255,255,255,0.18)" />
            <stop offset="55%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <linearGradient id="topSeal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#B8A87F" />
            <stop offset="100%" stopColor="#8F7F58" />
          </linearGradient>
          <linearGradient id="espressoInk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6B4423" />
            <stop offset="60%" stopColor="#4A2D18" />
            <stop offset="100%" stopColor="#2A1F13" />
          </linearGradient>
        </defs>

        <path d="M55 20 L345 20 L360 55 L40 55 Z" fill="url(#topSeal)" stroke="#8F7F58" strokeWidth="1" />
        <circle cx="200" cy="34" r="6" fill="#8F7F58" stroke="#6B5A38" strokeWidth="1" />

        <path d="M40 55 L360 55 L370 505 L30 505 Z" fill="url(#body)" stroke="#8F7F58" strokeWidth="1" />
        <path d="M40 55 L360 55 L370 505 L30 505 Z" fill="url(#sheen)" />

        <rect x="52" y="75" width="296" height="410" fill="none" stroke="#6B5A38" strokeWidth="1" />
        <rect x="58" y="81" width="284" height="398" fill="none" stroke="#4A2D18" strokeWidth="0.5" opacity="0.6" />

        {[
          [58, 81, 0],
          [342, 81, 90],
          [342, 479, 180],
          [58, 479, 270],
        ].map(([x, y, r]) => (
          <g key={`corner-${x}-${y}`} transform={`translate(${x} ${y}) rotate(${r})`}>
            <path d="M0 12 L0 0 L12 0" fill="none" stroke="#4A2D18" strokeWidth="1.4" />
          </g>
        ))}

        {/* Coffee-leaf mark in plantation green */}
        <g transform="translate(200 155)">
          <ellipse cx="0" cy="0" rx="40" ry="26" fill="none" stroke="#2E5D3F" strokeWidth="1.6" transform="rotate(-30)" />
          <ellipse cx="0" cy="0" rx="40" ry="26" fill="none" stroke="#2E5D3F" strokeWidth="1.6" transform="rotate(30)" />
          <circle cx="0" cy="0" r="8" fill="#6B4423" />
          <circle cx="0" cy="0" r="5" fill="#2A1F13" />
        </g>

        <line x1="130" y1="205" x2="270" y2="205" stroke="#6B5A38" strokeWidth="0.8" />
        <circle cx="200" cy="205" r="2" fill="#4A2D18" />

        <text x="200" y="245" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="700" fontSize="30" letterSpacing="3" fill="url(#espressoInk)">
          RUDRA ROAST
        </text>
        <text x="200" y="272" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="500" fontSize="16" letterSpacing="8" fill="#6B4423">
          COFFEE
        </text>
        <text x="200" y="298" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="12" fill="#2E5D3F" letterSpacing="6">❦</text>

        <text x="200" y="335" textAnchor="middle" fontFamily="Fraunces, serif" fontStyle="italic" fontSize="22" fill="#2A1F13">
          {name}
        </text>
        <text x="200" y="360" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="8" letterSpacing="2.5" fill="#4A2D18">
          {notes.toUpperCase()}
        </text>
        {composition && (
          <text x="200" y="410" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="8" letterSpacing="2" fill="#2E5D3F">
            {composition}
          </text>
        )}

        <line x1="80" y1="440" x2="320" y2="440" stroke="#6B5A38" strokeWidth="0.6" />
        <rect x="150" y="452" width="100" height="20" fill="none" stroke="#4A2D18" strokeWidth="1" />
        <text x="200" y="466" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="11" letterSpacing="3" fill="#2A1F13">
          NET WT · {weightLabel}
        </text>
      </svg>

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
    </div>
  );
}
